import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  CaretLeft, CaretRight, DownloadSimple, Lightning, Warning, CheckCircle,
  ArrowsLeftRight, Sun, Moon, Coffee, CalendarBlank, FunnelSimple, GearSix,
  Plus, Trash, Clock, FilePdf
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DAY_ABBR = ['L','M','X','J','V','S','D'];

const ESTADO_COLORS = {
  asignado: '',
  descanso: 'bg-blue-50 border-blue-200',
  libre: 'bg-zinc-50 border-zinc-200',
  manual_override: '',
  no_contratado: 'bg-zinc-100',
};

const TIPO_COLORS = {
  dia: 'bg-amber-500 text-white',
  tarde: 'bg-indigo-600 text-white',
};

const ShiftsPageV2 = () => {
  const { axiosInstance, isAdmin, user } = useAuth();
  const canEdit = isAdmin || user?.role === 'encargado_turno' || user?.role === 'jefe_local';
  const [branches, setBranches] = useState([]);
  const [cargos, setCargos] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedCargo, setSelectedCargo] = useState('');
  const now = new Date();
  const [monthYear, setMonthYear] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [gridData, setGridData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generatedResult, setGeneratedResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [turnoConfigsList, setTurnoConfigsList] = useState([]);
  const [horarios, setHorarios] = useState([]);
  const [newTurno, setNewTurno] = useState({ nombre: '', hora_inicio: '', hora_fin: '', tipo: 'dia', cargo_ids: [], dias: null });
  const [editingTurno, setEditingTurno] = useState(null);
  const scheduleVersion = useRef(0);
  const [lastEditor, setLastEditor] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportCargos, setExportCargos] = useState([]);

  const loadBranches = useCallback(async () => {
    try {
      const [bRes, cRes] = await Promise.all([
        axiosInstance.get('/api/branches'),
        axiosInstance.get('/api/cargos'),
      ]);
      setBranches(bRes.data);
      setCargos(cRes.data.filter(c => c.is_active));
      if (bRes.data.length > 0 && !selectedBranch) {
        const ub = bRes.data.find(b => b.id === user?.branch_id);
        setSelectedBranch(ub?.id || bRes.data[0].id);
      }
    } catch (e) { /* silenced */ }
  }, [axiosInstance, user, selectedBranch]);

  useEffect(() => { loadBranches(); }, [loadBranches]);

  const loadGrid = useCallback(async () => {
    if (!selectedBranch) return;
    try {
      setLoading(true);
      const params = { branch_id: selectedBranch, year: monthYear.year, month: monthYear.month };
      if (selectedCargo) params.cargo_id = selectedCargo;
      const res = await axiosInstance.get('/api/asignacion-turnos', { params });
      setGridData(res.data);
      setTurnoConfigsList(res.data.turno_configs || []);
      setHorarios(res.data.horarios || []);
      scheduleVersion.current = res.data.schedule_version || 0;
    } catch (e) {
      toast.error('Error al cargar turnos');
    } finally { setLoading(false); }
  }, [axiosInstance, selectedBranch, monthYear, selectedCargo]);

  useEffect(() => { if (selectedBranch) loadGrid(); }, [loadGrid, selectedBranch]);

  // Poll for changes by other users every 10s
  useEffect(() => {
    if (!selectedBranch) return;
    const interval = setInterval(async () => {
      try {
        const res = await axiosInstance.get('/api/turnos/version', {
          params: { branch_id: selectedBranch, year: monthYear.year, month: monthYear.month },
        });
        const serverVersion = res.data.version || 0;
        if (serverVersion > scheduleVersion.current && scheduleVersion.current > 0) {
          const editor = res.data.updated_by_name || 'otro usuario';
          setLastEditor(editor);
          toast.info(`Turnos actualizados por ${editor}. Recargando...`);
          scheduleVersion.current = serverVersion;
          loadGrid();
        }
      } catch (e) { /* silenced */ }
    }, 10000);
    return () => clearInterval(interval);
  }, [axiosInstance, selectedBranch, monthYear, loadGrid]);

  const navigateMonth = (dir) => {
    let { year, month } = monthYear;
    month += dir;
    if (month > 12) { month = 1; year++; }
    if (month < 1) { month = 12; year--; }
    setMonthYear({ year, month });
  };

  const handleCellChange = async (workerId, date, turnoConfigId) => {
    try {
      const estado = turnoConfigId === '__descanso' ? 'descanso' : (turnoConfigId ? 'asignado' : 'libre');
      const tcId = (turnoConfigId === '__descanso' || !turnoConfigId) ? null : turnoConfigId;
      const res = await axiosInstance.put('/api/asignacion-turnos/celda', {
        trabajador_id: workerId,
        fecha: date,
        turno_config_id: tcId,
        estado: estado,
      });
      if (res.data.warnings?.length > 0) {
        res.data.warnings.forEach(w => toast.warning(w));
      }
      loadGrid();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axiosInstance.post('/api/turnos/generar-mensual', {
        branch_id: selectedBranch,
        year: monthYear.year,
        month: monthYear.month,
        cargo_id: selectedCargo || null,
      });
      setGeneratedResult(res.data);
      setShowGenerateModal(true);
    } catch (e) {
      toast.error('Error al generar turnos');
    } finally { setGenerating(false); }
  };

  const handleApplyGeneration = async () => {
    if (!generatedResult) return;
    setApplying(true);
    try {
      const asignaciones = generatedResult.asignaciones.map(a => ({
        trabajador_id: a.trabajador_id,
        fecha: a.fecha,
        turno_config_id: a.turno_config_id,
        estado: a.estado,
      }));
      const res = await axiosInstance.post('/api/turnos/aplicar-generacion', { asignaciones });
      toast.success(`Turnos aplicados: ${res.data.created} creados, ${res.data.updated} actualizados`);
      setShowGenerateModal(false);
      setGeneratedResult(null);
      loadGrid();
    } catch (e) {
      toast.error('Error al aplicar turnos');
    } finally { setApplying(false); }
  };

  const handleCreateTurnoConfig = async () => {
    if (!newTurno.nombre || !newTurno.hora_inicio || !newTurno.hora_fin) {
      toast.error('Complete todos los campos');
      return;
    }
    try {
      await axiosInstance.post('/api/turno-configs', {
        branch_id: selectedBranch,
        nombre: newTurno.nombre,
        hora_inicio: newTurno.hora_inicio,
        hora_fin: newTurno.hora_fin,
        tipo: newTurno.tipo,
        cargo_ids: newTurno.cargo_ids.length > 0 ? newTurno.cargo_ids : null,
        dias: newTurno.dias && newTurno.dias.length > 0 ? newTurno.dias : null,
      });
      toast.success('Turno creado');
      setNewTurno({ nombre: '', hora_inicio: '', hora_fin: '', tipo: 'dia', cargo_ids: [], dias: null });
      loadGrid();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear turno');
    }
  };

  const handleDeleteTurnoConfig = async (configId) => {
    try {
      await axiosInstance.delete(`/api/turno-configs/${configId}`);
      toast.success('Turno eliminado');
      loadGrid();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleEditTurnoConfig = (tc) => {
    setEditingTurno({
      id: tc.id,
      nombre: tc.nombre,
      hora_inicio: tc.hora_inicio,
      hora_fin: tc.hora_fin,
      tipo: tc.tipo,
      cargo_ids: tc.cargo_ids || (tc.cargo_id ? [tc.cargo_id] : []),
      dias: tc.dias || null,
    });
  };

  const handleSaveEditTurno = async () => {
    if (!editingTurno) return;
    try {
      await axiosInstance.put(`/api/turno-configs/${editingTurno.id}`, {
        nombre: editingTurno.nombre,
        hora_inicio: editingTurno.hora_inicio,
        hora_fin: editingTurno.hora_fin,
        tipo: editingTurno.tipo,
        cargo_ids: editingTurno.cargo_ids.length > 0 ? editingTurno.cargo_ids : [],
        dias: editingTurno.dias && editingTurno.dias.length > 0 ? editingTurno.dias : null,
      });
      toast.success('Turno actualizado');
      setEditingTurno(null);
      loadGrid();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al actualizar');
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      params.append('branch_id', selectedBranch);
      params.append('year', monthYear.year);
      params.append('month', monthYear.month);
      if (exportCargos.length > 0) {
        params.append('cargo_ids', exportCargos.join(','));
      }
      const res = await axiosInstance.get(`/api/turnos/exportar-pdf?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `turnos_${monthYear.year}_${String(monthYear.month).padStart(2,'0')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
      setShowExportModal(false);
    } catch (e) {
      toast.error('Error al exportar PDF');
    }
  };

  const handleSaveHorarios = async (horariosData) => {
    try {
      await axiosInstance.put(`/api/horario-funcionamiento/${selectedBranch}`, {
        branch_id: selectedBranch,
        horarios: horariosData.map((h, i) => ({
          branch_id: selectedBranch,
          dia_semana: i,
          hora_apertura: h.hora_apertura || '10:00',
          hora_cierre: h.hora_cierre || '23:00',
          activo: h.activo ?? true,
        })),
      });
      toast.success('Horarios guardados');
      loadGrid();
    } catch (e) {
      toast.error('Error al guardar horarios');
    }
  };

  const branchName = branches.find(b => b.id === selectedBranch)?.name || '';
  const turnoConfigs = gridData?.turno_configs || [];
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-4" data-testid="shifts-page-v2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900">Turnos</h1>
          <p className="text-sm text-zinc-500 mt-1">Planificacion mensual de turnos · {branchName}</p>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button onClick={() => setShowConfig(!showConfig)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 transition-colors"
              data-testid="config-btn">
              <GearSix size={18} />
              Configurar
            </button>
          )}
          {canEdit && turnoConfigsList.length > 0 && (
            <button onClick={handleGenerate} disabled={generating}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              data-testid="generate-btn">
              <Lightning size={18} weight="fill" />
              {generating ? 'Generando...' : 'Generar Turnos'}
            </button>
          )}
          <button onClick={() => { setExportCargos([]); setShowExportModal(true); }}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-red-300 bg-white text-red-700 hover:bg-red-50 transition-colors"
            data-testid="export-pdf-btn">
            <FilePdf size={18} weight="fill" />
            Exportar PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        {isAdmin && (
          <select value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)}
            className="form-select text-sm w-48" data-testid="branch-filter">
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select value={selectedCargo} onChange={(e) => setSelectedCargo(e.target.value)}
          className="form-select text-sm w-40" data-testid="cargo-filter">
          <option value="">Todos los cargos</option>
          {cargos.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <button onClick={() => navigateMonth(-1)} className="p-1.5 border border-zinc-200 hover:bg-zinc-50" data-testid="prev-month">
            <CaretLeft size={16} />
          </button>
          <span className="text-sm font-medium text-zinc-700 min-w-[160px] text-center" data-testid="month-label">
            {MONTH_NAMES[monthYear.month - 1]} {monthYear.year}
          </span>
          <button onClick={() => navigateMonth(1)} className="p-1.5 border border-zinc-200 hover:bg-zinc-50" data-testid="next-month">
            <CaretRight size={16} />
          </button>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-4 ml-auto text-[11px]">
          <span className="flex items-center gap-1.5"><span className="w-5 h-5 bg-orange-400 inline-block border border-orange-500"></span> <strong>Dia</strong></span>
          <span className="flex items-center gap-1.5"><span className="w-5 h-5 bg-blue-600 inline-block border border-blue-700"></span> <strong>Tarde</strong></span>
          <span className="flex items-center gap-1.5"><span className="w-5 h-5 bg-cyan-100 inline-block border border-cyan-400"></span> <strong>Descanso</strong></span>
          <span className="flex items-center gap-1.5"><span className="w-5 h-5 bg-gray-200 inline-block border border-gray-300"></span> <strong>Sin asignar</strong></span>
        </div>
      </div>

      {/* Config Panel */}
      {showConfig && canEdit && (
        <ConfigPanel
          turnoConfigs={turnoConfigsList}
          horarios={horarios}
          newTurno={newTurno}
          setNewTurno={setNewTurno}
          onCreateTurno={handleCreateTurnoConfig}
          onDeleteTurno={handleDeleteTurnoConfig}
          onSaveHorarios={handleSaveHorarios}
          branchName={branchName}
          cargos={cargos}
          editingTurno={editingTurno}
          setEditingTurno={setEditingTurno}
          onEditTurno={handleEditTurnoConfig}
          onSaveEditTurno={handleSaveEditTurno}
        />
      )}

      {/* No turnos warning */}
      {!loading && turnoConfigsList.length === 0 && (
        <div className="border border-amber-200 bg-amber-50 p-6 text-center space-y-3" data-testid="no-turnos-warning">
          <Warning size={32} className="text-amber-500 mx-auto" />
          <p className="text-sm text-amber-800 font-medium">No hay turnos configurados para {branchName}</p>
          <p className="text-xs text-amber-600">Configure al menos un turno de tipo "Dia" y uno de tipo "Tarde" para poder generar la planificacion mensual.</p>
          {canEdit && !showConfig && (
            <button onClick={() => setShowConfig(true)}
              className="mt-2 px-4 py-2 text-sm bg-amber-600 text-white hover:bg-amber-700 transition-colors"
              data-testid="open-config-btn">
              <GearSix size={16} className="inline mr-1" /> Configurar Turnos
            </button>
          )}
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>
      ) : gridData ? (
        <MonthlyGrid
          data={gridData}
          turnoConfigs={turnoConfigs}
          canEdit={canEdit}
          onCellChange={handleCellChange}
          today={today}
        />
      ) : (
        <p className="text-sm text-zinc-400 text-center py-8">Sin datos</p>
      )}

      {/* Export PDF Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FilePdf size={22} className="text-red-600" weight="fill" />
              Exportar Turnos en PDF
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-zinc-600">
              Se exportara la planilla de <strong>{MONTH_NAMES[monthYear.month - 1]} {monthYear.year}</strong> de <strong>{branchName}</strong>.
            </p>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2 block">Seleccionar cargos a exportar</label>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setExportCargos([])}
                  className={`px-3 py-1.5 text-xs border transition-colors ${exportCargos.length === 0 ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'}`}
                  data-testid="export-all-cargos">
                  Todos
                </button>
                {cargos.map(c => {
                  const sel = exportCargos.includes(c.id);
                  return (
                    <button key={c.id}
                      onClick={() => {
                        if (sel) {
                          setExportCargos(exportCargos.filter(x => x !== c.id));
                        } else {
                          setExportCargos([...exportCargos, c.id]);
                        }
                      }}
                      className={`px-3 py-1.5 text-xs border transition-colors ${sel ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'}`}
                      data-testid={`export-cargo-${c.id}`}>
                      {c.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-[10px] text-zinc-400 mt-2">
                {exportCargos.length === 0 ? 'Se incluiran todos los cargos' : `${exportCargos.length} cargo(s) seleccionado(s)`}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportModal(false)} className="rounded-none">Cancelar</Button>
            <Button onClick={handleExportPDF} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-export-pdf">
              <FilePdf size={16} className="mr-1" weight="fill" /> Descargar PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Modal */}
      <Dialog open={showGenerateModal} onOpenChange={(open) => { if (!open) { setShowGenerateModal(false); setGeneratedResult(null); } }}>
        <DialogContent className="sm:max-w-3xl rounded-none border-zinc-200 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Lightning size={22} className="text-emerald-600" weight="fill" />
              Propuesta de Turnos - {MONTH_NAMES[monthYear.month - 1]} {monthYear.year}
            </DialogTitle>
          </DialogHeader>
          {generatedResult && (
            <GenerationPreview result={generatedResult} turnoConfigs={turnoConfigs} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowGenerateModal(false); setGeneratedResult(null); }} className="rounded-none">
              Cancelar
            </Button>
            <Button onClick={handleApplyGeneration} disabled={applying}
              className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="apply-gen-btn">
              {applying ? 'Aplicando...' : 'Aplicar Turnos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ─── Monthly Grid Component ───
const MonthlyGrid = ({ data, turnoConfigs, canEdit, onCellChange, today }) => {
  const { dates, workers, days_in_month, year, month } = data;
  const tcMap = {};
  turnoConfigs.forEach(t => { tcMap[t.id] = t; });

  return (
    <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="monthly-grid-v2">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-zinc-50 border-b border-zinc-200">
            <th className="text-left px-2 py-2 text-[10px] uppercase tracking-wider font-medium text-zinc-500 sticky left-0 bg-zinc-50 z-10 min-w-[150px] border-r border-zinc-200">
              Trabajador
            </th>
            {dates.map((d, i) => {
              const dt = new Date(d + 'T12:00:00');
              const dow = dt.getDay();
              const isSun = dow === 0;
              const isSat = dow === 6;
              const isToday = d === today;
              return (
                <th key={d} className={`text-center px-0 py-1.5 font-medium min-w-[36px] border-r border-zinc-100 ${isSun ? 'bg-red-50 text-red-600' : isSat ? 'bg-amber-50/50 text-amber-700' : 'text-zinc-400'} ${isToday ? 'ring-2 ring-inset ring-emerald-400' : ''}`}>
                  <div className="text-[9px]">{DAY_ABBR[dow === 0 ? 6 : dow - 1]}</div>
                  <div className="text-[10px]">{i + 1}</div>
                </th>
              );
            })}
            <th className="text-center px-1 py-1.5 text-[10px] font-medium text-zinc-500 min-w-[35px] border-r border-zinc-100">Hrs</th>
            <th className="text-center px-1 py-1.5 text-[10px] font-medium text-zinc-500 min-w-[30px] border-r border-zinc-100">D/T</th>
            <th className="text-center px-1 py-1.5 text-[10px] font-medium text-zinc-500 min-w-[30px]">Dom</th>
          </tr>
        </thead>
        <tbody>
          {workers.map((w) => (
            <tr key={w.id} className="border-b border-zinc-100 hover:bg-zinc-50/30">
              <td className="px-2 py-1.5 sticky left-0 bg-white z-10 border-r border-zinc-200">
                <div className="font-medium text-zinc-900 text-[11px] truncate max-w-[140px]">{w.name}</div>
                <div className="text-[9px] text-zinc-400">{w.code} · {w.cargo}</div>
              </td>
              {dates.map((d) => {
                const dayData = w.days[d] || {};
                const estado = dayData.estado;
                const isPast = d < today;
                const dt = new Date(d + 'T12:00:00');
                const isSun = dt.getDay() === 0;
                const isToday = d === today;

                if (estado === 'no_contratado') {
                  return <td key={d} className="text-center bg-zinc-100 border-r border-zinc-100 text-[9px] text-zinc-300">-</td>;
                }

                const tc = dayData.turno_config_id ? tcMap[dayData.turno_config_id] : null;

                let cellContent;
                let bgClass = '';

                if (estado === 'asignado' && tc) {
                  const isDia = tc.tipo === 'dia';
                  bgClass = isDia ? 'bg-orange-400' : 'bg-blue-600';
                  cellContent = (
                    <div className="text-white text-[9px] font-bold leading-tight py-1" title={`${tc.nombre}: ${tc.hora_inicio}-${tc.hora_fin}`}>
                      <div>{isDia ? 'D' : 'T'}</div>
                      <div className="font-normal text-[8px] opacity-90">{tc.hora_inicio}</div>
                    </div>
                  );
                } else if (estado === 'descanso') {
                  bgClass = 'bg-cyan-100';
                  cellContent = <div className="text-cyan-700 text-[9px] font-bold py-1">X</div>;
                } else if (estado === 'libre') {
                  bgClass = 'bg-gray-100';
                  cellContent = <div className="text-gray-400 text-[8px] py-1">libre</div>;
                } else {
                  // Sin asignar (punto)
                  bgClass = isPast ? 'bg-gray-50' : 'bg-yellow-50';
                  cellContent = <div className={`text-[8px] py-1 ${isPast ? 'text-gray-300' : 'text-yellow-500 font-medium'}`}>{isPast ? '-' : 'S/A'}</div>;
                }

                return (
                  <td key={d} className={`text-center border-r border-zinc-100 px-0 py-0 ${bgClass} ${isToday ? 'ring-2 ring-inset ring-emerald-500' : ''}`}>
                    {canEdit && !isPast ? (
                      <select
                        value={estado === 'asignado' && dayData.turno_config_id ? dayData.turno_config_id : (estado === 'descanso' ? '__descanso' : '')}
                        onChange={(e) => onCellChange(w.id, d, e.target.value)}
                        className={`w-full text-[9px] text-center border-0 py-0 px-0 focus:ring-0 cursor-pointer appearance-none font-bold ${
                          estado === 'asignado' && tc?.tipo === 'dia' ? 'bg-orange-400 text-white' :
                          estado === 'asignado' && tc?.tipo === 'tarde' ? 'bg-blue-600 text-white' :
                          estado === 'descanso' ? 'bg-cyan-100 text-cyan-700' :
                          'bg-yellow-50 text-yellow-600'
                        }`}
                        style={{ WebkitAppearance: 'none' }}
                        data-testid={`cell-${w.id}-${d}`}
                        title={tc ? `${tc.nombre}: ${tc.hora_inicio}-${tc.hora_fin}` : estado || 'Sin asignar'}
                      >
                        <option value="">S/A</option>
                        {turnoConfigs.filter(t => t.activo !== false).map(t => (
                          <option key={t.id} value={t.id}>{t.tipo === 'dia' ? 'D' : 'T'} {t.hora_inicio?.slice(0,5)}</option>
                        ))}
                        <option value="__descanso">X desc</option>
                      </select>
                    ) : (
                      cellContent
                    )}
                  </td>
                );
              })}
              <td className={`text-center px-1 py-1 font-mono text-[10px] font-bold border-r border-zinc-100 ${w.stats.max_weekly_hours > 42 ? 'text-red-600' : 'text-zinc-700'}`}>
                {w.stats.total_hours}
              </td>
              <td className="text-center px-1 py-1 text-[10px] border-r border-zinc-100">
                <span className="text-amber-600">{w.stats.dia_count}</span>/<span className="text-indigo-600">{w.stats.tarde_count}</span>
              </td>
              <td className={`text-center px-1 py-1 text-[10px] font-bold ${w.stats.free_sundays < 2 ? 'text-red-600' : 'text-emerald-600'}`}>
                {w.stats.free_sundays}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── Generation Preview ───
const GenerationPreview = ({ result, turnoConfigs }) => {
  const r = result;
  return (
    <div className="space-y-4" data-testid="gen-preview">
      <div className="grid grid-cols-3 gap-3">
        <div className="border border-zinc-200 p-3 text-center">
          <div className="text-2xl font-bold font-mono text-zinc-900">{r.total_workers}</div>
          <div className="text-xs text-zinc-500">Trabajadores</div>
        </div>
        <div className="border border-zinc-200 p-3 text-center">
          <div className="text-2xl font-bold font-mono text-emerald-600">{r.total_assignments}</div>
          <div className="text-xs text-zinc-500">Turnos asignados</div>
        </div>
        <div className="border border-zinc-200 p-3 text-center">
          <div className="text-2xl font-bold font-mono text-blue-600">{r.total_descansos}</div>
          <div className="text-xs text-zinc-500">Descansos</div>
        </div>
      </div>

      {/* Worker stats */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Resumen por trabajador</h3>
        <div className="border border-zinc-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <th className="text-left px-3 py-2 font-medium text-zinc-500">Trabajador</th>
                <th className="text-center px-2 py-2 font-medium text-zinc-500">Horas</th>
                <th className="text-center px-2 py-2 font-medium text-zinc-500">Dias</th>
                <th className="text-center px-2 py-2 font-medium text-zinc-500">Dia/Tarde</th>
                <th className="text-center px-2 py-2 font-medium text-zinc-500">Dom. Libres</th>
                <th className="text-center px-2 py-2 font-medium text-zinc-500">Max Sem.</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(r.stats).map((s) => (
                <tr key={s.worker_id} className="border-b border-zinc-100">
                  <td className="px-3 py-1.5">
                    <span className="text-zinc-400 font-mono mr-1">{s.code}</span>
                    <span className="font-medium text-zinc-900">{s.name}</span>
                    <span className="text-zinc-400 ml-1">· {s.cargo}</span>
                  </td>
                  <td className="text-center px-2 py-1.5 font-mono font-bold">{s.total_hours}h</td>
                  <td className="text-center px-2 py-1.5 font-mono">{s.days_worked}</td>
                  <td className="text-center px-2 py-1.5">
                    <span className="text-amber-600">{s.dia_count}</span>/<span className="text-indigo-600">{s.tarde_count}</span>
                  </td>
                  <td className={`text-center px-2 py-1.5 font-bold ${s.free_sundays < 2 ? 'text-red-600' : 'text-emerald-600'}`}>
                    {s.free_sundays}
                  </td>
                  <td className={`text-center px-2 py-1.5 font-mono ${s.max_weekly_hours > 42 ? 'text-red-600 font-bold' : ''}`}>
                    {s.max_weekly_hours}h
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {r.warnings.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-2">Advertencias</h3>
          <div className="space-y-1">
            {r.warnings.map((w, i) => (
              <div key={`w-${i}`} className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 text-xs text-amber-800">
                <Warning size={14} className="shrink-0" /> {w}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Config Panel ───
const ConfigPanel = ({ turnoConfigs, horarios, newTurno, setNewTurno, onCreateTurno, onDeleteTurno, onSaveHorarios, branchName, cargos, editingTurno, setEditingTurno, onEditTurno, onSaveEditTurno }) => {
  const DIA_NAMES = ['Lunes','Martes','Miercoles','Jueves','Viernes','Sabado','Domingo'];
  const defaultHorarios = DIA_NAMES.map((_, i) => ({ dia_semana: i, hora_apertura: '10:00', hora_cierre: '23:00', activo: true }));
  const [localHorarios, setLocalHorarios] = useState(defaultHorarios);

  useEffect(() => {
    if (horarios && horarios.length > 0) {
      const merged = DIA_NAMES.map((_, i) => {
        const existing = horarios.find(h => h.dia_semana === i);
        if (existing && (existing.hora_apertura || existing.id)) {
          return { ...existing, activo: existing.activo ?? true };
        }
        return { dia_semana: i, hora_apertura: '10:00', hora_cierre: '23:00', activo: true };
      });
      setLocalHorarios(merged);
    }
  }, [horarios]);

  return (
    <div className="border border-zinc-200 bg-white p-4 space-y-4" data-testid="config-panel">
      <h3 className="text-sm font-bold text-zinc-900">Configuracion de {branchName}</h3>

      {/* Turno Configs */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Turnos disponibles</h4>
        <div className="space-y-1.5 mb-3">
          {turnoConfigs.map(tc => {
            const tcCargos = tc.cargo_ids?.length > 0 ? tc.cargo_ids : (tc.cargo_id ? [tc.cargo_id] : []);
            const cargoNames = tcCargos.length > 0
              ? tcCargos.map(cid => cargos.find(c => c.id === cid)?.name || '?').join(', ')
              : 'Todos los cargos';
            const isEditing = editingTurno?.id === tc.id;

            if (isEditing) {
              return (
                <div key={tc.id} className="px-3 py-3 border border-emerald-300 bg-emerald-50 space-y-2" data-testid={`editing-tc-${tc.id}`}>
                  <div className="flex items-end gap-2">
                    <div className="flex-1">
                      <label className="text-[10px] text-zinc-500">Nombre</label>
                      <input type="text" value={editingTurno.nombre}
                        onChange={(e) => setEditingTurno({ ...editingTurno, nombre: e.target.value })}
                        className="form-input text-sm py-1" data-testid="edit-tc-nombre" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500">Inicio</label>
                      <input type="time" value={editingTurno.hora_inicio}
                        onChange={(e) => setEditingTurno({ ...editingTurno, hora_inicio: e.target.value })}
                        className="form-input text-sm py-1 font-mono" data-testid="edit-tc-inicio" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500">Fin</label>
                      <input type="time" value={editingTurno.hora_fin}
                        onChange={(e) => setEditingTurno({ ...editingTurno, hora_fin: e.target.value })}
                        className="form-input text-sm py-1 font-mono" data-testid="edit-tc-fin" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500">Tipo</label>
                      <select value={editingTurno.tipo}
                        onChange={(e) => setEditingTurno({ ...editingTurno, tipo: e.target.value })}
                        className="form-select text-sm py-1" data-testid="edit-tc-tipo">
                        <option value="dia">Dia</option>
                        <option value="tarde">Tarde</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500">Cargos</label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {cargos.map(c => {
                        const sel = (editingTurno.cargo_ids || []).includes(c.id);
                        return (
                          <button key={c.id} type="button"
                            onClick={() => {
                              const cur = editingTurno.cargo_ids || [];
                              setEditingTurno({ ...editingTurno, cargo_ids: sel ? cur.filter(x => x !== c.id) : [...cur, c.id] });
                            }}
                            className={`px-2 py-0.5 text-[10px] border transition-colors ${sel ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400'}`}>
                            {c.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500">Dias de la semana <span className="text-zinc-400">(dejar vacio = todos)</span></label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {DIA_NAMES.map((name, i) => {
                        const diasArr = editingTurno.dias || [];
                        const sel = diasArr.includes(i);
                        return (
                          <button key={`edit-dia-${i}`} type="button"
                            onClick={() => {
                              const cur = editingTurno.dias || [];
                              const updated = sel ? cur.filter(x => x !== i) : [...cur, i];
                              setEditingTurno({ ...editingTurno, dias: updated.length > 0 ? updated : null });
                            }}
                            className={`px-2 py-0.5 text-[10px] border transition-colors ${sel ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400'}`}
                            data-testid={`edit-dia-${i}`}>
                            {name.slice(0,3)}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[9px] text-zinc-400 mt-0.5">
                      {(!editingTurno.dias || editingTurno.dias.length === 0) ? 'Aplica todos los dias' : `${editingTurno.dias.length} dia(s) seleccionado(s)`}
                    </p>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={onSaveEditTurno}
                      className="px-3 py-1 text-xs bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                      data-testid="save-edit-tc-btn">
                      Guardar
                    </button>
                    <button onClick={() => setEditingTurno(null)}
                      className="px-3 py-1 text-xs border border-zinc-300 text-zinc-600 hover:bg-zinc-50 transition-colors"
                      data-testid="cancel-edit-tc-btn">
                      Cancelar
                    </button>
                  </div>
                </div>
              );
            }

            return (
            <div key={tc.id} className="flex items-center justify-between px-3 py-2 border border-zinc-100 bg-zinc-50">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`px-2 py-0.5 text-xs font-bold text-white ${tc.tipo === 'dia' ? 'bg-amber-500' : 'bg-indigo-600'}`}>
                  {tc.tipo === 'dia' ? 'DIA' : 'TARDE'}
                </span>
                <span className="text-sm font-medium">{tc.nombre}</span>
                <span className="text-xs text-zinc-400 font-mono">{tc.hora_inicio} - {tc.hora_fin}</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-zinc-200 text-zinc-600">{cargoNames}</span>
                {tc.dias && tc.dias.length > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700">
                    {tc.dias.map(d => DIA_NAMES[d]?.slice(0,3)).join(', ')}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => onEditTurno(tc)} className="text-zinc-400 hover:text-zinc-700 transition-colors p-1" data-testid={`edit-tc-${tc.id}`} title="Editar">
                  <GearSix size={14} />
                </button>
                <button onClick={() => onDeleteTurno(tc.id)} className="text-zinc-400 hover:text-red-600 transition-colors p-1" data-testid={`del-tc-${tc.id}`} title="Eliminar">
                  <Trash size={14} />
                </button>
              </div>
            </div>
            );
          })}
        </div>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-[10px] text-zinc-500">Nombre</label>
            <input type="text" value={newTurno.nombre} onChange={(e) => setNewTurno({ ...newTurno, nombre: e.target.value })}
              className="form-input text-sm py-1" placeholder="Ej: Manana" data-testid="new-tc-nombre" />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Inicio</label>
            <input type="time" value={newTurno.hora_inicio} onChange={(e) => setNewTurno({ ...newTurno, hora_inicio: e.target.value })}
              className="form-input text-sm py-1 font-mono" data-testid="new-tc-inicio" />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Fin</label>
            <input type="time" value={newTurno.hora_fin} onChange={(e) => setNewTurno({ ...newTurno, hora_fin: e.target.value })}
              className="form-input text-sm py-1 font-mono" data-testid="new-tc-fin" />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Tipo</label>
            <select value={newTurno.tipo} onChange={(e) => setNewTurno({ ...newTurno, tipo: e.target.value })}
              className="form-select text-sm py-1" data-testid="new-tc-tipo">
              <option value="dia">Dia</option>
              <option value="tarde">Tarde</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Cargo</label>
            <select value={newTurno.cargo_id || ''} onChange={(e) => setNewTurno({ ...newTurno, cargo_id: e.target.value })}
              className="form-select text-sm py-1" data-testid="new-tc-cargo">
              <option value="">Todos</option>
              {cargos.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <button onClick={onCreateTurno}
            className="px-3 py-1.5 text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center gap-1 shrink-0"
            data-testid="add-tc-btn">
            <Plus size={14} /> Agregar
          </button>
        </div>
        <div className="mt-2">
          <label className="text-[10px] text-zinc-500">Cargos (dejar vacio = todos)</label>
          <div className="flex flex-wrap gap-1 mt-1">
            {cargos.map(c => {
              const sel = (newTurno.cargo_ids || []).includes(c.id);
              return (
                <button key={c.id} type="button"
                  onClick={() => {
                    const cur = newTurno.cargo_ids || [];
                    setNewTurno({ ...newTurno, cargo_ids: sel ? cur.filter(x => x !== c.id) : [...cur, c.id] });
                  }}
                  className={`px-2 py-0.5 text-[10px] border transition-colors ${sel ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400'}`}
                  data-testid={`tc-cargo-${c.id}`}>
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>
        <div className="mt-2">
          <label className="text-[10px] text-zinc-500">Dias de la semana (dejar vacio = todos)</label>
          <div className="flex flex-wrap gap-1 mt-1">
            {DIA_NAMES.map((name, i) => {
              const diasArr = newTurno.dias || [];
              const sel = diasArr.includes(i);
              return (
                <button key={`new-dia-${i}`} type="button"
                  onClick={() => {
                    const cur = newTurno.dias || [];
                    const updated = sel ? cur.filter(x => x !== i) : [...cur, i];
                    setNewTurno({ ...newTurno, dias: updated.length > 0 ? updated : null });
                  }}
                  className={`px-2 py-0.5 text-[10px] border transition-colors ${sel ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400'}`}
                  data-testid={`new-dia-${i}`}>
                  {name.slice(0,3)}
                </button>
              );
            })}
          </div>
          <p className="text-[9px] text-zinc-400 mt-0.5">
            {(!newTurno.dias || newTurno.dias.length === 0) ? 'Aplica todos los dias' : `${newTurno.dias.length} dia(s) seleccionado(s)`}
          </p>
        </div>
      </div>

      {/* Horarios */}
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2">Horario de funcionamiento</h4>
        <div className="grid grid-cols-7 gap-1.5">
          {DIA_NAMES.map((name, i) => {
            const h = localHorarios[i] || { activo: false, hora_apertura: '10:00', hora_cierre: '23:00' };
            return (
              <div key={name} className={`border p-2 text-center space-y-1 ${h.activo ? 'border-emerald-200 bg-emerald-50/50' : 'border-zinc-200 bg-zinc-50'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-semibold text-zinc-700">{name.slice(0,3)}</span>
                  <input type="checkbox" checked={h.activo || false}
                    onChange={(e) => {
                      const updated = [...localHorarios];
                      updated[i] = { ...updated[i], activo: e.target.checked };
                      setLocalHorarios(updated);
                    }}
                    className="h-3 w-3 accent-emerald-600" />
                </div>
                <div>
                  <label className="text-[8px] text-zinc-400">Apertura</label>
                  <input type="time" value={h.hora_apertura || '10:00'}
                    onChange={(e) => { const u = [...localHorarios]; u[i] = { ...u[i], hora_apertura: e.target.value }; setLocalHorarios(u); }}
                    className={`w-full text-[10px] font-mono border border-zinc-200 px-1 py-0.5 text-center ${!h.activo ? 'opacity-40' : ''}`} />
                  <label className="text-[8px] text-zinc-400">Cierre</label>
                  <input type="time" value={h.hora_cierre || '23:00'}
                    onChange={(e) => { const u = [...localHorarios]; u[i] = { ...u[i], hora_cierre: e.target.value }; setLocalHorarios(u); }}
                    className={`w-full text-[10px] font-mono border border-zinc-200 px-1 py-0.5 text-center ${!h.activo ? 'opacity-40' : ''}`} />
                </div>
              </div>
            );
          })}
        </div>
        <button onClick={() => onSaveHorarios(localHorarios)}
          className="mt-2 px-3 py-1.5 text-sm bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
          data-testid="save-horarios-btn">
          Guardar Horarios
        </button>
      </div>
    </div>
  );
};

export default ShiftsPageV2;
